import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { createDoctorRequest } from "@/lib/api";

const CALENDLY_URL = "https://calendly.com/shrutiguptahere/30min";

export default function DoctorRequest() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [concern, setConcern] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);

  const bookingUrl = useMemo(
    () => `${CALENDLY_URL}?name=${encodeURIComponent(saved?.name || "")}&email=${encodeURIComponent(saved?.email || "")}`,
    [saved]
  );

  async function onSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await createDoctorRequest({
        name: name.trim(),
        email: email.trim(),
        concern: concern.trim() || null,
        consent,
        stage: null,
      });
      setSaved(response);
    } catch (error) {
      const detail = error?.response?.data?.detail || "Couldn't save your spot. Try again?";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg text-[#1C1917]" data-testid="doctor-request-page">
      <Header />
      <main className="pt-28 sm:pt-36 pb-16">
        <section className="section-pad" data-testid="doctor-request-section">
          <div className="max-w-xl mx-auto px-5 sm:px-8">
            <div className="text-center">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917]" data-testid="doctor-request-heading">
                Let&apos;s match you with a doctor
              </h1>
              <p className="mt-6 text-base sm:text-lg text-[#44403C] leading-relaxed" data-testid="doctor-request-subhead">
                Book a free chat with your perimenopause companion, then get matched with a doctor for virtual advice. Available by request.
              </p>
            </div>

            <div className="mt-10 supple-card max-w-xl mx-auto" data-testid="doctor-request-card">
              {saved ? (
                <div className="flex flex-col items-center text-center py-6" data-testid="doctor-request-success">
                  <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FFF4EC] text-[#9A3F25] mb-4" aria-hidden="true">
                    <Check size={26} strokeWidth={2} />
                  </span>
                  <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#1C1917]">
                    Request received.
                  </h2>
                  <p className="mt-3 text-[#44403C] leading-relaxed">
                    Thank you, {saved.name}. Pick a time for your free video chat. Your meeting link is created when you book.
                  </p>
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-pill cta-primary w-full justify-center mt-7"
                    data-testid="doctor-request-booking-button"
                  >
                    Book my free chat <ArrowRight size={16} />
                  </a>
                  <Link to="/supplement-check" className="mt-5 text-sm font-semibold underline" data-testid="doctor-request-supplements-link">
                    Explore supplements
                  </Link>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-4" data-testid="doctor-request-form">
                  <div>
                    <label htmlFor="doctor-request-name" className="block text-sm font-medium text-[#44403C] mb-1.5">
                      Your name
                    </label>
                    <input
                      id="doctor-request-name"
                      type="text"
                      required
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="First name is fine"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#F1D9C6] outline-none text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                      data-testid="doctor-request-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="doctor-request-email" className="block text-sm font-medium text-[#44403C] mb-1.5">
                      Email address
                    </label>
                    <input
                      id="doctor-request-email"
                      type="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="We'll only use this to follow up on your chat"
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#F1D9C6] outline-none text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                      data-testid="doctor-request-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="doctor-request-concern" className="block text-sm font-medium text-[#44403C] mb-1.5">
                      What&apos;s bothering you most?
                    </label>
                    <textarea
                      id="doctor-request-concern"
                      value={concern}
                      onChange={(event) => setConcern(event.target.value)}
                      placeholder="Sleep, mood, hot flashes, irregular periods..."
                      className="w-full px-4 py-3 rounded-xl bg-white border border-[#F1D9C6] outline-none text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                      data-testid="doctor-request-concern-input"
                    />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer select-none pt-1">
                    <input
                      type="checkbox"
                      required
                      checked={consent}
                      onChange={(event) => setConsent(event.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#EC6A4A] cursor-pointer"
                      data-testid="doctor-request-consent-checkbox"
                    />
                    <span className="text-sm text-[#44403C] leading-relaxed">
                      I agree to share my quiz answers with the doctor I&apos;m matched with. See our Privacy Policy.
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="cta-pill cta-primary w-full justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="doctor-request-submit-button"
                  >
                    {submitting ? "Continue to booking" : "Continue to booking"}
                    <ArrowRight size={16} />
                  </button>
                  <p className="text-sm text-[#78716C] text-center" data-testid="doctor-request-disclaimer">
                    Educational guidance, not medical diagnosis.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}